import { useState } from 'react';
import { Button, Input, Select, SelectItem, Textarea, Chip } from '@heroui/react';
import { Save } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { ProjectWithCounts, UpdateProject } from '@estimator/shared';
import CompanyCombobox from '@/components/shared/CompanyCombobox';

interface OverviewTabProps {
  project: ProjectWithCounts;
}

const PROJECT_TYPES = [
  { value: 'RESIDENTIAL', label: 'Residential' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'INDUSTRIAL', label: 'Industrial' },
  { value: 'MIXED_USE', label: 'Mixed Use' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'HEALTHCARE', label: 'Healthcare' },
  { value: 'EDUCATION', label: 'Education' },
];

const PROJECT_STATUSES = [
  { value: 'NEW', label: 'New' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'REVIEW', label: 'Review' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ON_HOLD', label: 'On Hold' },
];

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
  NEW: 'default',
  IN_PROGRESS: 'primary',
  REVIEW: 'warning',
  COMPLETED: 'success',
  ON_HOLD: 'danger',
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs mb-1.5 font-medium" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </p>
  );
}

export default function OverviewTab({ project }: OverviewTabProps) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<UpdateProject>({
    client_id: project.client_id,
    specifier_id: project.specifier_id ?? null,
    project_name: project.project_name,
    address: project.address ?? '',
    project_type: project.project_type,
    status: project.status,
    general_notes: project.general_notes ?? '',
  });

  const [dirty, setDirty] = useState(false);

  const update = useMutation({
    mutationFn: async (body: UpdateProject) => {
      const { data } = await apiClient.patch<ProjectWithCounts>(`/projects/${project.id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      setDirty(false);
    },
  });

  function set<K extends keyof UpdateProject>(key: K, value: UpdateProject[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  function handleSave() {
    update.mutate(form);
  }

  const inputClassNames = {
    inputWrapper:
      'bg-[var(--bg-panel)] border border-[var(--border)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)] shadow-none',
    input: 'text-sm text-[var(--text-primary)]',
    label: 'text-xs text-[var(--text-secondary)]',
  };

  const selectClassNames = {
    trigger:
      'bg-[var(--bg-panel)] border border-[var(--border)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)] shadow-none',
    value: 'text-sm text-[var(--text-primary)]',
    label: 'text-xs text-[var(--text-secondary)]',
    popoverContent: 'bg-[var(--bg-panel)] border border-[var(--border)]',
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Project Overview
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Edit project details below. Changes are saved when you click Save Changes.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <Chip
            size="sm"
            color={STATUS_COLORS[project.status] ?? 'default'}
            variant="flat"
          >
            {PROJECT_STATUSES.find((s) => s.value === project.status)?.label ?? project.status}
          </Chip>

          <Button
            size="sm"
            color="primary"
            isDisabled={!dirty}
            isLoading={update.isPending}
            startContent={!update.isPending && <Save size={14} />}
            onPress={handleSave}
            className="font-medium"
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Error */}
      {update.isError && (
        <div
          className="rounded-lg border px-4 py-3 text-sm"
          style={{
            borderColor: 'var(--red)',
            backgroundColor: 'rgba(248,113,113,0.08)',
            color: 'var(--red)',
          }}
        >
          Failed to save changes. Please try again.
        </div>
      )}

      {/* Form Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Client */}
        <div>
          <CompanyCombobox value={form.client_id ?? null} onChange={(id) => set('client_id', id ?? undefined)} label="Client" isRequired />
        </div>

        {/* Specifier */}
        <div>
          <CompanyCombobox value={form.specifier_id ?? null} onChange={(id) => set('specifier_id', id ?? undefined)} label="Specifier (Architect/Designer)" />
        </div>

        {/* Project Name */}
        <div>
          <FieldLabel>Project Name</FieldLabel>
          <Input
            value={form.project_name ?? ''}
            onValueChange={(v) => set('project_name', v)}
            placeholder="Project name"
            size="sm"
            classNames={inputClassNames}
          />
        </div>

        {/* Address — spans both columns */}
        <div className="sm:col-span-2">
          <FieldLabel>Address</FieldLabel>
          <Input
            value={form.address ?? ''}
            onValueChange={(v) => set('address', v || null)}
            placeholder="Project address"
            size="sm"
            classNames={inputClassNames}
          />
        </div>

        {/* Project Type */}
        <div>
          <FieldLabel>Project Type</FieldLabel>
          <Select
            selectedKeys={form.project_type ? new Set([form.project_type]) : new Set()}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as UpdateProject['project_type'];
              if (val) set('project_type', val);
            }}
            size="sm"
            classNames={selectClassNames}
            aria-label="Project type"
          >
            {PROJECT_TYPES.map((t) => (
              <SelectItem
                key={t.value}
                classNames={{
                  base: 'text-[var(--text-primary)] data-[hover=true]:bg-[var(--bg-hover)]',
                }}
              >
                {t.label}
              </SelectItem>
            ))}
          </Select>
        </div>

        {/* Status */}
        <div>
          <FieldLabel>Status</FieldLabel>
          <Select
            selectedKeys={form.status ? new Set([form.status]) : new Set()}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as UpdateProject['status'];
              if (val) set('status', val);
            }}
            size="sm"
            classNames={selectClassNames}
            aria-label="Status"
          >
            {PROJECT_STATUSES.map((s) => (
              <SelectItem
                key={s.value}
                classNames={{
                  base: 'text-[var(--text-primary)] data-[hover=true]:bg-[var(--bg-hover)]',
                }}
              >
                {s.label}
              </SelectItem>
            ))}
          </Select>
        </div>

        {/* Total GFA — readonly */}
        <div>
          <FieldLabel>Total GFA (m²) — calculated</FieldLabel>
          <Input
            value={project.total_gfa != null ? String(project.total_gfa) : ''}
            isReadOnly
            placeholder="Calculated from areas"
            size="sm"
            classNames={{
              ...inputClassNames,
              inputWrapper:
                'bg-[var(--bg-base)] border border-[var(--border)] opacity-60 cursor-not-allowed shadow-none',
              input: 'text-sm font-ui text-[var(--text-secondary)]',
            }}
          />
        </div>

        {/* Spacer */}
        <div />

        {/* General Notes — spans both columns */}
        <div className="sm:col-span-2">
          <FieldLabel>General Notes</FieldLabel>
          <Textarea
            value={form.general_notes ?? ''}
            onValueChange={(v) => set('general_notes', v || null)}
            placeholder="Any additional project notes…"
            minRows={4}
            size="sm"
            classNames={{
              inputWrapper:
                'bg-[var(--bg-panel)] border border-[var(--border)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)] shadow-none',
              input: 'text-sm text-[var(--text-primary)] resize-y',
            }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-4 rounded-xl border p-4"
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
      >
        {[
          { label: 'Drawings', value: project.drawing_count },
          { label: 'Areas', value: project.area_count },
          { label: 'Materials', value: project.material_count },
          { label: 'Markups', value: project.markup_count },
        ].map(({ label, value }) => (
          <div key={label} className="text-center">
            <p className="text-2xl font-ui font-semibold" style={{ color: 'var(--accent)' }}>
              {value ?? 0}
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              {label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
